import { useState, useEffect } from 'react';
import { X, Copy, CircleNotch } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { API } from './constants';

/**
 * Dupliceer-modal: kopieert een campagne (sequence of broadcast) naar hetzelfde
 * of een ander profiel. De kopie start altijd als `draft` met lege stats —
 * de gebruiker activeert hem zelf wanneer alles klaarstaat.
 */
export function CampaignDuplicateModal({ campaign, onClose, onCreated }) {
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(`${campaign?.name || 'Campagne'} (kopie)`);
  const [targetProfile, setTargetProfile] = useState(campaign?.profile_id || '');

  useEffect(() => {
    setLoadingProfiles(true);
    fetch(`${API}/profiles`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { profiles: [] })
      .then(d => setProfiles(d.profiles || []))
      .catch(() => {})
      .finally(() => setLoadingProfiles(false));
  }, []);

  useEffect(() => {
    setName(`${campaign?.name || 'Campagne'} (kopie)`);
    setTargetProfile(campaign?.profile_id || '');
  }, [campaign?.campaign_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!name.trim()) {
      toast.error('Geef de nieuwe campagne een naam');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/email/campaigns/${campaign.campaign_id}/duplicate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          target_profile_id: targetProfile || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(`Dupliceren mislukt: ${err.detail || res.statusText}`);
        return;
      }
      const d = await res.json();
      const crossProfile = d.profile_id && d.profile_id !== campaign.profile_id;
      toast.success(crossProfile
        ? `Gekopieerd naar profiel "${d.profile_id}" — schakel daarheen om de campagne te activeren`
        : 'Campagne gedupliceerd — staat als concept klaar');
      onCreated?.(d);
    } catch (e) {
      toast.error(`Netwerkfout: ${e.message || 'onbekend'}`);
    }
    setSaving(false);
  };

  if (!campaign) return null;
  const crossProfile = targetProfile && targetProfile !== campaign.profile_id;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4"
      onClick={() => !saving && onClose()}
      data-testid="campaign-duplicate-modal"
    >
      <div
        className="bg-white w-full max-w-md shadow-2xl border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-200 gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 bg-purple-50 border border-purple-200 flex items-center justify-center shrink-0">
              <Copy size={18} className="text-purple-600" />
            </div>
            <div className="min-w-0">
              <h3 className="font-heading text-base font-semibold text-zinc-900">Campagne dupliceren</h3>
              <p className="text-[11px] text-zinc-500 mt-0.5 truncate">
                Bron: <span className="font-data">{campaign.campaign_id}</span> — {campaign.name}
              </p>
            </div>
          </div>
          <button
            onClick={() => !saving && onClose()}
            disabled={saving}
            data-testid="campaign-duplicate-close"
            className="p-1.5 hover:bg-zinc-100 disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="text-xs font-medium text-zinc-500 mb-1 block">Naam van de kopie</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              className="rounded-sm text-sm"
              data-testid="campaign-duplicate-name"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-500 mb-1 block">Doel-profiel</label>
            {loadingProfiles ? (
              <div className="text-xs text-zinc-400 flex items-center gap-2">
                <CircleNotch size={12} className="animate-spin" /> Profielen laden...
              </div>
            ) : (
              <Select value={targetProfile} onValueChange={setTargetProfile}>
                <SelectTrigger className="rounded-sm text-sm" data-testid="campaign-duplicate-profile-trigger">
                  <SelectValue placeholder="Kies profiel..." />
                </SelectTrigger>
                <SelectContent className="z-[90]">
                  {profiles.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-zinc-400">Geen profielen beschikbaar</div>
                  ) : profiles.map(p => (
                    <SelectItem
                      key={p.slug}
                      value={p.slug}
                      data-testid={`campaign-duplicate-profile-${p.slug}`}
                    >
                      <span className="font-medium">{p.name || p.slug}</span>
                      {p.slug === campaign.profile_id && (
                        <span className="ml-2 text-[10px] text-zinc-400">(huidig)</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {crossProfile && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">
              <strong>Let op:</strong> de kopie wordt aangemaakt onder profiel <strong>{targetProfile}</strong>.
              Verzendadres, contacten en zoektermen verschillen mogelijk — controleer voor activering.
            </div>
          )}

          <div className="text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 px-3 py-2">
            De kopie start altijd als <strong>concept</strong> met lege statistieken. Sequence-stappen,
            verzendadres, handtekening en segment-filter worden meegenomen.
          </div>
        </div>

        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50/60 flex items-center justify-end gap-2">
          <button
            onClick={() => !saving && onClose()}
            disabled={saving}
            className="px-3 py-1.5 text-xs text-zinc-600 hover:bg-zinc-100 transition-colors border border-zinc-200 disabled:opacity-50"
            data-testid="campaign-duplicate-cancel"
          >
            Annuleer
          </button>
          <button
            onClick={submit}
            disabled={saving || !name.trim()}
            className="px-3 py-1.5 text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
            data-testid="campaign-duplicate-submit"
          >
            {saving ? <CircleNotch size={12} className="animate-spin" /> : <Copy size={12} />}
            {saving ? 'Dupliceren...' : 'Dupliceer campagne'}
          </button>
        </div>
      </div>
    </div>
  );
}
